import axiosInstance from 'src/config/axios.config';

// ---- Kademeli filtre tipleri (backend dto/report/*OptionDto ile birebir) ----
export interface CompanyOption {
  id: string;
  name: string;
}

export interface BranchOption {
  id: string;
  name: string;
  companyId: string | null;
}

export interface EmployeeOption {
  id: string;
  fullName: string;
  branchId: string;
}

const FILTERS_BASE = '/api/reports/filters';

/** Kullanıcının görebildiği firmalar (rol'e göre backend daraltır). */
export async function getReportCompanies(): Promise<CompanyOption[]> {
  const res = await axiosInstance.get<CompanyOption[]>(`${FILTERS_BASE}/companies`);
  return res.data ?? [];
}

/** Kullanıcının görebildiği şubeler; companyId verilirse o firmaya filtrelenir. */
export async function getReportBranches(companyId?: string): Promise<BranchOption[]> {
  const res = await axiosInstance.get<BranchOption[]>(`${FILTERS_BASE}/branches`, {
    params: companyId ? { companyId } : undefined,
  });
  return res.data ?? [];
}

/** Verilen şubedeki çalışanlar (backend şubeye erişim yetkisini kontrol eder). */
export async function getReportEmployees(branchId: string): Promise<EmployeeOption[]> {
  const res = await axiosInstance.get<EmployeeOption[]>(`${FILTERS_BASE}/employees`, {
    params: { branchId },
  });
  return res.data ?? [];
}

const REPORTS_BASE = '/api/reports';

// ---- Aylık devam raporu tipleri ----
export type DayStatus = 'PRESENT' | 'ABSENT' | 'LEAVE' | 'OFF';

export interface MonthlyAttendanceCell {
  date: string; // ISO yyyy-MM-dd
  checkIn: string | null; // HH:mm:ss
  checkOut: string | null;
  workMinutes: number;
  dayStatus: DayStatus;
}

export interface MonthlyAttendanceRow {
  employeeId: string;
  fullName: string;
  branchName: string;
  days: MonthlyAttendanceCell[];
  totalMinutes: number;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
}

export interface MonthlyAttendanceMatrix {
  year: number;
  month: number;
  branchId: string;
  branchName: string;
  daysInMonth: number;
  rows: MonthlyAttendanceRow[];
}

/** Firma+şube filtreli aylık devam matrisi (çalışan × gün).
 *  branchId yoksa firma (veya yetki) bazlı tüm şubeler birleşir. */
export async function getMonthlyAttendance(
  companyId: string | null,
  branchId: string | null,
  year: number,
  month: number
): Promise<MonthlyAttendanceMatrix> {
  const res = await axiosInstance.get<MonthlyAttendanceMatrix>(`${REPORTS_BASE}/monthly-attendance`, {
    params: { companyId: companyId || undefined, branchId: branchId || undefined, year, month },
  });
  return res.data;
}

// ---- Düzenlilik (regularity) raporu tipleri ----
export type SignalType = 'LATE' | 'EARLY_LEAVE' | 'ABSENCE' | 'FORGOTTEN_CHECKOUT';

export interface RegularityEvent {
  date: string;
  type: SignalType;
  deviationMinutes: number | null;
  checkIn: string | null;
  checkOut: string | null;
  note: string | null;
}

export interface EmployeeRegularity {
  employeeId: string;
  fullName: string;
  branchId: string;
  branchName: string;
  lateCount: number;
  earlyLeaveCount: number;
  absenceCount: number;
  forgottenCheckoutCount: number;
  expectedWorkdays: number;
  score: number;
  events: RegularityEvent[];
}

export interface BranchRegularityRanking {
  branchId: string;
  branchName: string;
  ranking: EmployeeRegularity[];
}

export interface RegularityReport {
  start: string;
  end: string;
  irregularFirst: boolean;
  branches: BranchRegularityRanking[];
  overall: EmployeeRegularity[];
}

export type RegularityMode = 'irregular' | 'regular';

export interface RegularityParams {
  companyId?: string | null;
  branchId?: string | null;
  start: string;
  end: string;
}

/** En düzenli / en düzensiz çalışan raporu. */
export async function getRegularity(
  mode: RegularityMode,
  params: RegularityParams
): Promise<RegularityReport> {
  const res = await axiosInstance.get<RegularityReport>(`${REPORTS_BASE}/regularity/${mode}`, {
    params: {
      companyId: params.companyId || undefined,
      branchId: params.branchId || undefined,
      start: params.start,
      end: params.end,
    },
  });
  return res.data;
}

// ---- Tek-personel raporu tipleri ----
export interface DailyWorkPoint {
  date: string;
  hours: number;
}

export interface SingleEmployeeReport {
  regularity: EmployeeRegularity;
  workHours: DailyWorkPoint[];
}

/** Tek-personel raporu: düzenlilik özeti + günlük çalışma saatleri. */
export async function getEmployeeReport(
  employeeId: string,
  start: string,
  end: string
): Promise<SingleEmployeeReport> {
  const res = await axiosInstance.get<SingleEmployeeReport>(`${REPORTS_BASE}/employee/${employeeId}`, {
    params: { start, end },
  });
  return res.data;
}

// ---- Rol-bilinçli dashboard ----
export interface RoleDashboard {
  role: string;
  scopeLabel: string;
  companyCount: number;
  branchCount: number;
  employeeCount: number;
  totalLate: number;
  totalEarlyLeave: number;
  totalAbsence: number;
  totalForgotten: number;
  topIrregular: EmployeeRegularity[];
}

/** Rol-bilinçli ana dashboard özeti (kapsam + KPI + en düzensiz çalışanlar). */
export async function getRoleOverview(): Promise<RoleDashboard> {
  const res = await axiosInstance.get<RoleDashboard>(`${REPORTS_BASE}/dashboard/role-overview`);
  return res.data;
}

// ---- Dashboard insights (trend + şube karnesi) ----
export interface TrendMetric {
  key: 'attendanceRate' | 'absence' | 'late' | 'workedHours';
  current: number;
  previous: number;
  deltaPercent: number;
  series: number[];
  labels: string[];
}

export interface BranchScorecardRow {
  branchId: string;
  branchName: string;
  employeeCount: number;
  attendanceRate: number;
  avgRegularityScore: number;
  absenceCount: number;
  lateCount: number;
  rank: number;
}

export interface DashboardInsights {
  attendanceRate: TrendMetric;
  absence: TrendMetric;
  late: TrendMetric;
  workedHours: TrendMetric;
  branchScorecard: BranchScorecardRow[];
  singleBranch: boolean;
}

/** Ana ekran iş zekâsı: ay-üzeri trend + şube karnesi (bu ay). */
export async function getDashboardInsights(): Promise<DashboardInsights> {
  const res = await axiosInstance.get<DashboardInsights>(`${REPORTS_BASE}/dashboard/insights`);
  return res.data;
}

/** Şube karnesi (tarih aralıklı, firma/şube filtreli). */
export async function getBranchScorecard(
  companyId: string | null,
  branchId: string | null,
  start: string,
  end: string
): Promise<BranchScorecardRow[]> {
  const res = await axiosInstance.get<BranchScorecardRow[]>(`${REPORTS_BASE}/branch-scorecard`, {
    params: { companyId: companyId || undefined, branchId: branchId || undefined, start, end },
  });
  return res.data ?? [];
}

export type ExportFormat = 'PDF' | 'EXCEL';
const EXPORT_BASE = '/api/export/reports';

/** Şube karnesini PDF/Excel indirir. */
export async function exportBranchScorecard(
  companyId: string | null,
  branchId: string | null,
  start: string,
  end: string,
  format: ExportFormat
): Promise<Blob> {
  const res = await axiosInstance.get(`${EXPORT_BASE}/branch-scorecard`, {
    params: { companyId: companyId || undefined, branchId: branchId || undefined, start, end, format },
    responseType: 'blob',
  });
  return res.data as Blob;
}

/** Tek-personel raporunu PDF/Excel indirir (gün-gün olaylar). */
export async function exportEmployeeReport(
  employeeId: string,
  start: string,
  end: string,
  format: ExportFormat
): Promise<Blob> {
  const res = await axiosInstance.get(`${EXPORT_BASE}/employee`, {
    params: { employeeId, start, end, format },
    responseType: 'blob',
  });
  return res.data as Blob;
}

/** Düzenlilik raporunu PDF/Excel olarak indirir. */
export async function exportRegularity(
  mode: RegularityMode,
  params: RegularityParams,
  format: ExportFormat
): Promise<Blob> {
  const res = await axiosInstance.get(`${EXPORT_BASE}/regularity`, {
    params: {
      format,
      regular: mode === 'regular',
      companyId: params.companyId || undefined,
      branchId: params.branchId || undefined,
      start: params.start,
      end: params.end,
    },
    responseType: 'blob',
  });
  return res.data as Blob;
}

/** Aylık devam raporunu PDF/Excel olarak indirir (blob). */
export async function exportMonthlyAttendance(
  companyId: string | null,
  branchId: string | null,
  year: number,
  month: number,
  format: ExportFormat
): Promise<Blob> {
  const res = await axiosInstance.get(`${EXPORT_BASE}/monthly-attendance`, {
    params: { companyId: companyId || undefined, branchId: branchId || undefined, year, month, format },
    responseType: 'blob',
  });
  return res.data as Blob;
}
